import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DocumentConfigPartial } from '@domain';
import { environment } from '@environments';
import { debounceTime } from 'rxjs';
import { RaIconComponent, RaSwitchComponent } from 'ra-ui';
import { RaLoadingComponent } from '@shared/components';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import {
    RaSelectModel,
    RaSelectOptionModel,
    withSelectedOption,
} from 'src/app/shared/components/ra-select/model/ra-select.model';
import { RaSelectComponent } from 'src/app/shared/components/ra-select/ra-select.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DOCUMENT_FONT_FAMILY_OPTIONS } from '../../../constants/document-font-select-options.generated';
import { FALLBACK_CURRENCY, resolveCurrency } from 'src/app/shared/constants/currency.constant';
import { nearestImageGrid, resolveSingleImageSize } from '../../../../../pdf-renderer/image-size';
import {
    CAPTION_POSITION_BELOW,
    CAPTION_POSITION_SIDE,
    CAPTION_STYLES,
    DEFAULT_CAPTION_STYLE,
    normalizeCaptionStyle,
    resolveCaptionSide,
} from '../../../../../pdf-renderer/caption-style';
import { imageSizeFromWidth, imageSizeWidth } from '../../cards/image-card/image-size.util';
import { ImageWidthFieldComponent } from '../../image-width-field/image-width-field.component';
import { MediaFillFieldComponent } from '../../media-fill-field/media-fill-field.component';
import { MEDIA_FILL_DEFAULT, parseMediaFill } from '../../../../../media-fill-options';
import {
    LookGlyphComponent,
    LookGlyphName,
} from '../../cards/_card-base/look-glyph/look-glyph.component';

/** Payload enviado ao salvar — espelha o merge `copyWith` do app mobile. */
export type DocumentConfigFormValue = DocumentConfigPartial;

/** Ordem fixa de exibição das moedas — espelha o picker do app mobile. */
const CURRENCY_ORDER = ['BRL', 'USD', 'EUR', 'GBP'];

/**
 * Layout do orçamento. Espelha QuoteLayoutEnum da API e o `quoteLayout` que o PdfEngine lê:
 * `table` é o padrão de documento novo, e qualquer outro valor (inclusive ausente, que é o
 * documento anterior a esta config) cai em `list`.
 */
const QUOTE_LAYOUT_LIST = 'list';
const QUOTE_LAYOUT_TABLE = 'table';
const QUOTE_LAYOUT_OPTIONS = [QUOTE_LAYOUT_TABLE, QUOTE_LAYOUT_LIST];

/** Chaves do formulário que os blocos das seções de imagens e vídeos trocam direto. */
type LookKey =
    | 'fullImage'
    | 'captionStyle'
    | 'captionSide'
    | 'showImageNumber'
    | 'showImageDate'
    | 'showImageWatermark'
    | 'showVideo';

/** 'table' => 'Table', para compor a chave de tradução da opção. */
const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

@Component({
    selector: 'modal-document-config',
    templateUrl: 'modal-document-config.component.html',
    styleUrl: 'modal-document-config.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RaModalComponent,
        RaButtonComponent,
        RaSelectComponent,
        RaSwitchComponent,
        RaIconComponent,
        RaLoadingComponent,
        ImageWidthFieldComponent,
        LookGlyphComponent,
        MediaFillFieldComponent,
        TranslatePipe,
    ],
})
export class ModalDocumentConfigComponent {
    private _translate = inject(TranslateService);

    /**
     * Semeia o form a partir do `config` apenas uma vez (na abertura). O documento é
     * realtime: presença/online-users reemitem um novo objeto de config (com os MESMOS
     * valores salvos) a cada poucos segundos, e re-aplicar isso resetaria os
     * switches/seleções em andamento. O modal é recriado a cada abertura (`@if` no
     * header), então semear uma vez basta.
     */
    private _formSeeded = false;

    /** Empurra o modal para o lado quando o editor de footer abre por cima. */
    public pushed = input<boolean>(false);
    /** Config atual do documento — estado local do formulário é derivado daqui ao abrir/atualizar. */
    public config = input.required<DocumentConfigPartial>();
    /**
     * O usuário mexeu em alguma configuração desde que abriu o modal. Não indica
     * "não salvo" (o auto-save já gravou no documento): habilita o botão Salvar, que
     * pergunta onde mais as alterações devem valer. Quem controla é o pai, que também
     * enxerga os saves de capa/cabeçalho/rodapé.
     */
    public hasChanges = input<boolean>(false);
    /** Loading do botão Salvar enquanto o alcance é propagado. */
    public savingScope = input<boolean>(false);
    /** Moeda só aparece em orçamento — é a única espécie com preços e totais. */
    public isQuote = input<boolean>(false);

    public mainModal = viewChild<RaModalComponent>('mainModal');

    public close = output();
    public save = output<DocumentConfigFormValue>();
    /** Pedido de escolher o alcance das alterações (abre o modal de escopo). */
    public saveScope = output();
    /**
     * Emite o novo grid quando o usuário troca "imagens por linha" (só quando muda).
     * O pai decide se pergunta para aplicar a todos os grupos existentes.
     */
    public gridChanged = output<number>();

    /** Editor do cabeçalho ou do rodapé abrindo (semeando o padrão): loading no lápis. */
    public openingEditor = input<'header' | 'footer' | null>(null);
    /** Abrir o editor de conteúdo do rodapé (footer). */
    public editFooter = output();
    /** Abrir o editor de conteúdo do cabeçalho (header). */
    public editHeader = output();
    /** Abrir o editor de conteúdo da capa (cover). */
    public editCover = output();

    public form = new FormGroup({
        showCover: new FormControl(true, { nonNullable: true }),
        showHeader: new FormControl(true, { nonNullable: true }),
        showFooter: new FormControl(true, { nonNullable: true }),
        showSummary: new FormControl(true, { nonNullable: true }),
        sectionPageBreak: new FormControl(true, { nonNullable: true }),
        fontFamily: new FormControl<string>(environment.DEFAULT_FONT_FAMILY, { nonNullable: true }),
        lineSpacing: new FormControl<number>(1.5, { nonNullable: true }),
        fontSize: new FormControl<number>(12, { nonNullable: true }),
        textAlign: new FormControl<string>(TEXT_ALIGN_JUSTIFY, { nonNullable: true }),
        currencyCode: new FormControl<string>(FALLBACK_CURRENCY.code, { nonNullable: true }),
        quoteLayout: new FormControl<string>(QUOTE_LAYOUT_TABLE, { nonNullable: true }),
        showImageNumber: new FormControl(true, { nonNullable: true }),
        showImageDate: new FormControl(true, { nonNullable: true }),
        fullImage: new FormControl(false, { nonNullable: true }),
        /** Cor do fundo das mídias inteiras (#rrggbb); o padrão não vai para a config. */
        imageFillColor: new FormControl<string>(MEDIA_FILL_DEFAULT, { nonNullable: true }),
        showImageWatermark: new FormControl(false, { nonNullable: true }),
        showVideo: new FormControl(true, { nonNullable: true }),
        groupImageGrid: new FormControl<number>(2, { nonNullable: true }),
        singleImageGrid: new FormControl<number>(1, { nonNullable: true }),
        /** "Personalizado": largura da foto em % (null = vale o singleImageGrid). */
        imageWidth: new FormControl<number | null>(null),
        singleVideoGrid: new FormControl<number>(1, { nonNullable: true }),
        /** Estilo da legenda das fotos e dos vídeos (card, figure, band). */
        captionStyle: new FormControl<string>(DEFAULT_CAPTION_STYLE, { nonNullable: true }),
        /** Descrição ao lado da foto única e do vídeo; salva como `captionPosition`. */
        captionSide: new FormControl(false, { nonNullable: true }),
    });

    /**
     * O formulário como signal, para os blocos das seções de imagens e vídeos: a app não tem
     * zone.js, e ler `form.controls.x.value` no template não redesenharia depois da semeadura
     * (que é `emitEvent: false`). Atualizado em toda mudança e depois de semear.
     */
    public look = signal(this.form.getRawValue());

    /** Tamanhos prontos, fotos por linha e tamanhos da miniatura: 1 a 3. */
    public readonly sizeOptions = [1, 2, 3];
    /** Desenhos das opções, os mesmos do painel da foto. */
    public sizeGlyph(grid: number): LookGlyphName {
        return `size-${grid}` as LookGlyphName;
    }

    public colsGlyph(count: number): LookGlyphName {
        return `cols-${count}` as LookGlyphName;
    }

    public captionGlyph(style: string): LookGlyphName {
        return `caption-${style}` as LookGlyphName;
    }
    public readonly captionStyles = CAPTION_STYLES;

    /** Largura mostrada no campo "Personalizado": a livre, senão a do tamanho pronto. */
    public singleImageWidthValue = computed(() => {
        const state = this.look();
        return (
            state.imageWidth ??
            imageSizeWidth({ grid: this._clampGrid(state.singleImageGrid), width: null })
        );
    });

    public loading = input<boolean>(false);
    // Signals: o `ra-select` só reflete a mudança quando recebe uma referência
    // nova — marcar `option.selected` no mesmo objeto deixava o campo mostrando o
    // valor anterior (ou o placeholder, na abertura).
    public fontSelectModel = signal<RaSelectModel>(null);
    public lineSpacingSelectModel = signal<RaSelectModel>(null);
    public textSizeSelectModel = signal<RaSelectModel>(null);
    public textAlignSelectModel = signal<RaSelectModel>(null);
    public currencySelectModel = signal<RaSelectModel>(null);
    public quoteLayoutSelectModel = signal<RaSelectModel>(null);

    constructor() {
        this.fontSelectModel.set(this._buildFontSelectModel());
        this.lineSpacingSelectModel.set(this._buildLineSpacingSelectModel());
        this.textSizeSelectModel.set(this._buildTextSizeSelectModel());
        this.textAlignSelectModel.set(this._buildTextAlignSelectModel());
        this.currencySelectModel.set(this._buildCurrencySelectModel());
        this.quoteLayoutSelectModel.set(this._buildQuoteLayoutSelectModel());
        this._applyTranslatedSelectTexts();

        effect(() => {
            const c = this.config();
            untracked(() => this._applyTranslatedSelectTexts());

            // Só sincroniza o form na primeira emissão (abertura). Ver `_formSeeded`.
            if (this._formSeeded) return;
            this._formSeeded = true;

            const font = (c.fontFamily as string) || environment.DEFAULT_FONT_FAMILY;
            const lineSpacing = this._normalizeLineSpacing(c.lineSpacing);
            const fontSize = this._normalizeTextSize(c.fontSize);
            const textAlign = this._normalizeTextAlign(c.textAlign);
            const grid = this._clampGrid(c.groupImageGrid ?? 2);
            const singleSize = resolveSingleImageSize([c]);
            const singleGrid = singleSize.grid;
            const videoGrid = this._clampGrid(c.singleVideoGrid ?? 1);
            const captionStyle = normalizeCaptionStyle(c.captionStyle);
            const currency = resolveCurrency(c.currencyCode).code;
            // Campo ausente = documento anterior à config: abre em lista, que é como ele
            // já é renderizado. Semear `table` aqui mudaria o documento no primeiro auto-save.
            const quoteLayout = this._normalizeQuoteLayout(c.quoteLayout);
            this.form.patchValue(
                {
                    showCover: c.showCover ?? true,
                    showHeader: c.showHeader ?? true,
                    showFooter: c.showFooter ?? true,
                    showSummary: c.showSummary ?? true,
                    sectionPageBreak: c.sectionPageBreak ?? true,
                    fontFamily: font,
                    lineSpacing,
                    fontSize,
                    textAlign,
                    currencyCode: currency,
                    quoteLayout,
                    showImageNumber: c.showImageNumber ?? true,
                    showImageDate: c.showImageDate ?? true,
                    fullImage: c.fullImage ?? false,
                    imageFillColor: parseMediaFill(c.imageFillColor) ?? MEDIA_FILL_DEFAULT,
                    showImageWatermark: c.showImageWatermark ?? false,
                    showVideo: c.showVideo ?? true,
                    groupImageGrid: grid,
                    singleImageGrid: singleGrid,
                    imageWidth: singleSize.width,
                    singleVideoGrid: videoGrid,
                    captionStyle,
                    captionSide: resolveCaptionSide([c]),
                },
                { emitEvent: false }
            );
            this.look.set(this.form.getRawValue());
            this._updateFontSelectSelected(font);
            this._updateLineSpacingSelected(lineSpacing);
            this._updateTextSizeSelected(fontSize);
            this._updateTextAlignSelected(textAlign);
            this._updateCurrencySelected(currency);
            this._updateQuoteLayoutSelected(quoteLayout);
        });

        // Auto-save com debounce (espelha o app mobile): persiste sozinho ao mudar
        // qualquer flag/seleção. A semeadura usa `emitEvent: false`, então não dispara.
        // 400 ms: mesma janela do modal por-card. Antes eram 800 ms, e os cards
        // atrás do modal só refletiam número/data/grid quase um segundo depois.
        this.form.valueChanges
            .pipe(debounceTime(400), takeUntilDestroyed())
            .subscribe(() => this._emitSave());

        // Os blocos redesenham na hora; o save segue com o debounce acima.
        this.form.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.look.set(this.form.getRawValue()));
    }

    /**
     * Desfaz o fechamento quando a guarda de saída do pai é cancelada. Sem isto o
     * modal fica montado porém já animado para fora, travando a tela no fundo escuro.
     */
    public cancelClose(): void {
        this.mainModal()?.cancelClose();
    }

    public onFontSelect(option: RaSelectOptionModel): void {
        this.form.controls.fontFamily.setValue(option.value);
        this._updateFontSelectSelected(option.value);
    }

    public onLineSpacingSelect(option: RaSelectOptionModel): void {
        this.form.controls.lineSpacing.setValue(Number(option.value));
        this._updateLineSpacingSelected(Number(option.value));
    }

    public onTextSizeSelect(option: RaSelectOptionModel): void {
        this.form.controls.fontSize.setValue(Number(option.value));
        this._updateTextSizeSelected(Number(option.value));
    }

    public onTextAlignSelect(option: RaSelectOptionModel): void {
        this.form.controls.textAlign.setValue(String(option.value));
        this._updateTextAlignSelected(String(option.value));
    }

    /** Fotos por linha dos grupos novos; mudou, o pai pergunta sobre os grupos que já existem. */
    public chooseGroupImageGrid(columns: number): void {
        const grid = this._clampGrid(columns);
        const previous = this._clampGrid(this.form.controls.groupImageGrid.value);
        // Autosave persiste o novo default (vale para grupos NOVOS).
        this.form.controls.groupImageGrid.setValue(grid);
        // Só pergunta sobre aplicar aos grupos existentes quando o valor mudou.
        if (grid !== previous) this.gridChanged.emit(grid);
    }

    /**
     * Tamanho pronto da imagem única do documento. Diferente do grid de grupos, não há
     * "aplicar aos existentes": imagens sem override por-item já herdam esta config
     * na hora (item.configs?.singleImageGrid ?? config.singleImageGrid, no preview e no PDF).
     */
    public chooseSingleImageSize(grid: number): void {
        this._selectSingleImagePreset(grid);
    }

    /** Largura do "Personalizado". 100% é a imagem grande: volta para o tamanho pronto. */
    public onSingleImageWidth(width: number): void {
        const size = imageSizeFromWidth(width);
        if (size.width === null) {
            this._selectSingleImagePreset(size.grid);
            return;
        }

        this.form.controls.imageWidth.setValue(size.width);
    }

    private _selectSingleImagePreset(grid: number): void {
        this.form.controls.imageWidth.setValue(null);
        this.form.controls.singleImageGrid.setValue(this._clampGrid(grid));
    }

    /** Tamanho da miniatura do vídeo: mesma escala da imagem única. */
    public chooseVideoSize(grid: number): void {
        this.form.controls.singleVideoGrid.setValue(this._clampGrid(grid));
    }

    /** Enquadramento, legenda, número, data, marca d'água e vídeo no PDF pelos blocos. */
    public setLook(key: LookKey, value: boolean | string): void {
        const next = key === 'captionStyle' ? normalizeCaptionStyle(String(value)) : value;
        (this.form.controls[key] as FormControl<boolean | string>).setValue(next);
    }

    /**
     * Cor do fundo das mídias inteiras. O seletor livre manda cada cor enquanto é arrastado:
     * o save já espera a pessoa parar (debounce do auto-save).
     */
    public setFill(color: string): void {
        this.form.controls.imageFillColor.setValue(parseMediaFill(color) ?? MEDIA_FILL_DEFAULT);
    }

    /** Chave do nome do estilo da legenda (os mesmos do menu do rail). */
    public captionStyleLabel(style: string): string {
        return `document.edit.railLook.caption${capitalize(style)}`;
    }

    public onCurrencySelect(option: RaSelectOptionModel): void {
        this.form.controls.currencyCode.setValue(String(option.value));
        this._updateCurrencySelected(String(option.value));
    }

    public onQuoteLayoutSelect(option: RaSelectOptionModel): void {
        const layout = this._normalizeQuoteLayout(String(option.value));
        this.form.controls.quoteLayout.setValue(layout);
        this._updateQuoteLayoutSelected(layout);
    }

    /** Monta a config (base + valores do form) e emite o save (auto-save com debounce). */
    private _emitSave(): void {
        const raw = this.form.getRawValue();
        const base = this.config();
        this.save.emit({
            ...base,
            showCover: raw.showCover,
            showHeader: raw.showHeader,
            showFooter: raw.showFooter,
            showSummary: raw.showSummary,
            sectionPageBreak: raw.sectionPageBreak,
            fontFamily: raw.fontFamily,
            lineSpacing: this._normalizeLineSpacing(raw.lineSpacing),
            fontSize: this._normalizeTextSize(raw.fontSize),
            textAlign: this._normalizeTextAlign(raw.textAlign),
            currencyCode: raw.currencyCode,
            quoteLayout: raw.quoteLayout,
            showImageNumber: raw.showImageNumber,
            showImageDate: raw.showImageDate,
            fullImage: raw.fullImage,
            // O cinza padrão é a ausência da chave (a config do documento é trocada inteira).
            imageFillColor:
                raw.imageFillColor === MEDIA_FILL_DEFAULT ? undefined : parseMediaFill(raw.imageFillColor) ?? undefined,
            showImageWatermark: raw.showImageWatermark,
            showVideo: raw.showVideo,
            groupImageGrid: this._clampGrid(raw.groupImageGrid),
            // Com "Personalizado", o singleImageGrid leva o pronto mais perto (é o que o app
            // mostra). O update-config do documento troca a config inteira: `undefined` tira
            // a largura, e a foto volta ao tamanho pronto.
            singleImageGrid:
                raw.imageWidth !== null
                    ? nearestImageGrid(raw.imageWidth)
                    : this._clampGrid(raw.singleImageGrid),
            imageWidth: raw.imageWidth ?? undefined,
            singleVideoGrid: this._clampGrid(raw.singleVideoGrid),
            captionStyle: normalizeCaptionStyle(raw.captionStyle),
            captionPosition: raw.captionSide ? CAPTION_POSITION_SIDE : CAPTION_POSITION_BELOW,
        });
    }

    /**
     * Reescreve rótulos e placeholders no idioma atual. Cada modelo é reemitido
     * com uma referência nova — ver `withSelectedOption`.
     */
    private _applyTranslatedSelectTexts(): void {
        const tr = this._translate;

        /** Aplica placeholder + descrições preservando a opção marcada. */
        const retitle = (
            model: RaSelectModel,
            placeholderKey: string,
            describe: (option: RaSelectOptionModel, index: number) => string
        ): RaSelectModel => ({
            ...model,
            placeholder: tr.instant(placeholderKey),
            listOptions: (model.listOptions ?? []).map((option, index) => ({
                ...option,
                description: describe(option, index),
            })),
        });

        this.fontSelectModel.update(model =>
            retitle(
                model,
                'document.edit.documentConfig.fontPlaceholder',
                option => option.description
            )
        );

        this.lineSpacingSelectModel.update(model =>
            retitle(model, 'document.edit.documentConfig.lineSpacingPlaceholder', option =>
                tr.instant(
                    `document.edit.documentConfig.lineSpacing${String(option.value).replace('.', '')}`
                )
            )
        );

        this.textSizeSelectModel.update(model =>
            retitle(model, 'document.edit.documentConfig.textSizePlaceholder', option =>
                tr.instant(
                    `document.edit.documentConfig.textSize${String(option.value).replace('.', '')}`
                )
            )
        );

        this.textAlignSelectModel.update(model =>
            retitle(model, 'document.edit.documentConfig.textAlignPlaceholder', option =>
                tr.instant(
                    option.value === TEXT_ALIGN_LEFT
                        ? 'document.edit.documentConfig.textAlignLeft'
                        : 'document.edit.documentConfig.textAlignJustify'
                )
            )
        );

        // Só sigla e símbolo ("BRL · R$"). O nome por extenso ("Real brasileiro") estourava a
        // largura do campo e não acrescenta nada: quem escolhe a moeda reconhece o código.
        this.currencySelectModel.update(model =>
            retitle(model, 'document.edit.documentConfig.currencyPlaceholder', option => {
                const code = String(option.value);
                return `${code} · ${resolveCurrency(code).symbol}`;
            })
        );

        this.quoteLayoutSelectModel.update(model =>
            retitle(model, 'document.edit.documentConfig.quoteLayoutPlaceholder', option =>
                tr.instant(
                    `document.edit.documentConfig.quoteLayout${capitalize(String(option.value))}`
                )
            )
        );
    }

    private _buildFontSelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = DOCUMENT_FONT_FAMILY_OPTIONS.map(family => ({
            // Sufixo nas fontes aceitas pela norma (rótulo apenas; o valor
            // persistido continua o nome puro da família).
            description: ABNT_FONT_FAMILIES.includes(family) ? `${family} (ABNT)` : family,
            value: family,
            selected: false,
            fontFamily: family,
        }));
        return model;
    }

    private _updateFontSelectSelected(fontFamily: string): void {
        this.fontSelectModel.update(model => withSelectedOption(model, fontFamily));
    }

    private _buildQuoteLayoutSelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = QUOTE_LAYOUT_OPTIONS.map(layout => ({
            description: '',
            value: layout,
            selected: false,
        }));
        return model;
    }

    /** Qualquer valor fora do enum (inclusive ausente) é o layout de lista. */
    private _normalizeQuoteLayout(layout: string | undefined): string {
        return layout === QUOTE_LAYOUT_TABLE ? QUOTE_LAYOUT_TABLE : QUOTE_LAYOUT_LIST;
    }

    private _updateQuoteLayoutSelected(layout: string): void {
        this.quoteLayoutSelectModel.update(model => withSelectedOption(model, layout));
    }

    private _buildCurrencySelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = CURRENCY_ORDER.map(code => ({
            description: '',
            value: code,
            selected: false,
        }));
        return model;
    }

    private _updateCurrencySelected(code: string): void {
        this.currencySelectModel.update(model => withSelectedOption(model, code));
    }

    private _clampGrid(value: number): number {
        return Math.min(3, Math.max(1, Math.round(value)));
    }

    /** Opções estilo Google Docs. Os rótulos vêm do i18n (`lineSpacing1` etc.). */
    private _buildLineSpacingSelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = LINE_SPACING_OPTIONS.map(value => ({
            description: '',
            value,
            selected: false,
        }));
        return model;
    }

    private _updateLineSpacingSelected(value: number): void {
        this.lineSpacingSelectModel.update(model => withSelectedOption(model, value));
    }

    /** Valor fora da lista (dado antigo/corrompido) cai no default 1.5 do PDF. */
    private _normalizeLineSpacing(value: number | undefined | null): number {
        return LINE_SPACING_OPTIONS.includes(value as number) ? (value as number) : 1.5;
    }

    /** Só o texto normal é configurável; rótulos no i18n (`textSize10` etc.). */
    private _buildTextSizeSelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = TEXT_SIZE_OPTIONS.map(value => ({
            description: '',
            value,
            selected: false,
        }));
        return model;
    }

    private _updateTextSizeSelected(value: number): void {
        this.textSizeSelectModel.update(model => withSelectedOption(model, value));
    }

    /** Valor fora da lista (dado antigo/corrompido) cai no default 12 (ABNT). */
    private _normalizeTextSize(value: number | undefined | null): number {
        return TEXT_SIZE_OPTIONS.includes(value as number) ? (value as number) : 12;
    }

    /** Justificado ou à esquerda; rótulos no i18n (`textAlignJustify` / `textAlignLeft`). */
    private _buildTextAlignSelectModel(): RaSelectModel {
        const model = new RaSelectModel();
        model.placeholder = '';
        model.loading = false;
        model.disabled = false;
        model.listOptions = TEXT_ALIGN_OPTIONS.map(value => ({
            description: '',
            value,
            selected: false,
        }));
        return model;
    }

    private _updateTextAlignSelected(value: string): void {
        this.textAlignSelectModel.update(model => withSelectedOption(model, value));
    }

    /** Ausente ou desconhecido cai em justificado, o padrão histórico do PDF. */
    private _normalizeTextAlign(value: string | undefined | null): string {
        return value === TEXT_ALIGN_LEFT ? TEXT_ALIGN_LEFT : TEXT_ALIGN_JUSTIFY;
    }
}

/** Espaçamentos entre linhas oferecidos (multiplicador CSS), estilo Google Docs. */
export const LINE_SPACING_OPTIONS: readonly number[] = [1, 1.15, 1.5, 2];

/** Tamanhos do texto normal oferecidos, em pontos. 12 = padrão ABNT. */
export const TEXT_SIZE_OPTIONS: readonly number[] = [10, 10.5, 11, 12];

/** Alinhamento do texto no PDF: justificado (padrão de laudo) ou à esquerda. */
export const TEXT_ALIGN_JUSTIFY = 'justify';
export const TEXT_ALIGN_LEFT = 'left';
export const TEXT_ALIGN_OPTIONS: readonly string[] = [TEXT_ALIGN_JUSTIFY, TEXT_ALIGN_LEFT];

/** Fontes aceitas pela ABNT, marcadas com sufixo no seletor. */
export const ABNT_FONT_FAMILIES: readonly string[] = ['Arial', 'Times New Roman'];
